import React, { useState, useCallback, memo } from 'react';
import { Row, Col, Modal } from 'react-bootstrap';
import { FaInstagram, FaEnvelope, FaFacebook } from 'react-icons/fa';
import { useSelector, useDispatch } from 'react-redux';
import { fetchPrivacyPolicy } from '../redux/privacyPolicySlice';
import { fetchtermsAndConditions } from '../redux/termsAndConditionsSlice';
import DOMPurify from 'dompurify';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

// Import logo with explicit width and height for better CLS
const logo = {
  src: new URL('../images/logo1.png', import.meta.url).href,
  width: 120,
  height: 50
};

// Constants to avoid recreation on each render
const SOCIAL_LINKS = [
  { href: "https://www.instagram.com/lyricalwings", icon: FaInstagram, color: '#E1306C', label: "Instagram" },
  { href: "https://www.facebook.com/lyricalwings", icon: FaFacebook, color: '#1877F2', label: "Facebook" },
  { href: "mailto:lyricalwings@gmail.com", icon: FaEnvelope, color: '#222', label: "Email" }
];

const FOOTER_LINKS = [
  { text: 'Privacy Policy', modalContent: 'privacypolicy' },
  { text: 'Terms & Conditions', modalContent: 'terms&conditions' }
];

const NAV_LINKS = ['Home', 'Poems', 'Narrative'];

// Memoized Modal Content Components
const PrivacyPolicyContent = memo(({ policies }) => {
  if (policies.length === 0) return <p>Privacy Policy data is not available.</p>;
  
  return policies.map(policy => (
    <div key={policy.id}>
      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(policy.htmlContent) }} />
    </div>
  ));
});

const TermsContent = memo(({ terms }) => {
  if (terms.length === 0) return <p>Terms and Conditions data is not available.</p>;
  
  return terms.map(term => (
    <div key={term.id}>
      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(term.htmlContent) }} />
    </div>
  ));
});

function Footer() {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [hasFetched, setHasFetched] = useState({
    privacyPolicy: false,
    termsConditions: false,
  });

  const termsAndConditions = useSelector(state => state.termsAndConditions.policies);
  const privacyPolicy = useSelector(state => state.privacyPolicy.policies);
  const currentYear = new Date().getFullYear();

  const handleShowModal = useCallback((content) => {
    setModalContent(content);
    setShowModal(true);
    
    if (content === 'privacypolicy' && !hasFetched.privacyPolicy) {
      dispatch(fetchPrivacyPolicy());
      setHasFetched(prev => ({ ...prev, privacyPolicy: true }));
    }
    
    if (content === 'terms&conditions' && !hasFetched.termsConditions) {
      dispatch(fetchtermsAndConditions());
      setHasFetched(prev => ({ ...prev, termsConditions: true }));
    }
  }, [dispatch, hasFetched]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setModalContent('');
  }, []);

  // Memoized modal content renderer
  const renderModalContent = useCallback(() => {
    switch (modalContent) {
      case 'privacypolicy':
        return <PrivacyPolicyContent policies={privacyPolicy} />;
      case 'terms&conditions':
        return <TermsContent terms={termsAndConditions} />;
      default:
        return null;
    }
  }, [modalContent, privacyPolicy, termsAndConditions]);

  const modalTitle = modalContent === 'privacypolicy' 
    ? 'Privacy Policy' 
    : modalContent === 'terms&conditions'
      ? 'Terms & Conditions'
      : '';

  return (
    <>
      <footer className="bg-light text-dark py-3 border-top shadow">
        <Row className="px-2">
          {/* Social Media Column */}
          <Col md={3} className="text-center">
            <h6>Follow us</h6>
            <div className="d-flex justify-content-evenly gap-4">
              {SOCIAL_LINKS.map(({ href, icon: Icon, color, label }) => (
                <a 
                  key={label}
                  href={href} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon" 
                  style={{ color }}
                  aria-label={label}
                >
                  <Icon size={20} />
                </a>
              ))}
            </div>
            
            <div className="d-flex justify-content-center mt-2 mb-3">
              <div className="horizontal-divider w-75"></div>
            </div>

            <div className="justify-content-center d-flex gap-2 my-2 footer-link-xs fw-lighter">
              {FOOTER_LINKS.map(({ text, modalContent }) => (
                <button
                  key={text}
                  type="button"
                  style={{ fontSize: 'x-small', background: 'none', border: 'none', padding: '0 8px' }}
                  className="text-decoration-none text-dark footer-link" 
                  onClick={() => handleShowModal(modalContent)}
                >
                  {text}
                </button>
              ))}
            </div>
          </Col>
          
          {/* Navigation Links Column */}
          <Col md={6} className="d-flex justify-content-center gap-4">
            <Row>
              <div className="justify-content-center align-items-end d-flex gap-4">
                {NAV_LINKS.map((item) => (
                  <a
                    key={item}
                    href={`/${item.toLowerCase()}`}
                    className="footer-link text-dark text-decoration-none"
                  >
                    {item}
                  </a>
                ))}
              </div>
              <div className="d-flex align-items-end">
                <Col className="text-center">
                  <small>&copy; {currentYear} LyricalWings. All Rights Reserved.</small>
                </Col>
              </div>
            </Row>
          </Col>
          
          {/* Logo Column */}
          <Col md={3} className="text-center d-flex justify-content-center align-items-center">
            <LazyLoadImage 
              src={logo.src} 
              alt="LyricalWings Logo" 
              width={logo.width} 
              height={logo.height} 
              effect="blur"
              placeholderSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 50'%3E%3Crect width='120' height='50' fill='%23f0f0f0'/%3E%3C/svg%3E"
            />
          </Col>
        </Row>
      </footer>

      {/* Modal - Only rendered when needed */}
      {showModal && (
        <Modal centered size="lg" show={true} onHide={handleCloseModal} className="p-4">
          <Modal.Header closeButton>
            <Modal.Title className="text-black">{modalTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-5 py-3">{renderModalContent()}</Modal.Body>
        </Modal>
      )}
    </>
  );
}

export default memo(Footer);